// Composition layer that wires the AI suggestion cache to the openAI
// service for a given thought. Surfaces (e.g. DraftsReview) only need to
// hand a thought + the memoized allTags list — fetcher construction and
// guard checks live here.

import { suggestTagsForThought, hybridTagsEnabled } from './openAiService';
import { isPrefetchableText } from '../utils/draftPredicate';
import {
  prefetchTagSuggestions,
  abortTagSuggestionsForId,
  tagSuggestionMode,
} from '../utils/aiSuggestionsCache';

// Returns an abort function so React effect cleanups can cancel a
// stale prefetch the moment the user advances past the upcoming card.
// Without this, rapid card-stack swipes leave N concurrent in-flight
// requests, each costing tokens + a Vercel function invocation.
//
// `activeIdRef` (optional) lets the caller report the currently-active
// thought id at cleanup time. When the user advances onto the prefetched
// card, the cleanup's id matches the new active id and we keep the entry
// — ThoughtDetail is about to consume it via cachedSuggestTags. Without
// this guard, the cleanup aborts the cache entry the moment it becomes
// useful, forcing a redundant fresh fetch.
export const prefetchTagSuggestionsForThought = (
  thought,
  allTags,
  activeIdRef
) => {
  if (!thought || !isPrefetchableText(thought.thought)) return () => {};
  const existingTags = thought.tags || [];
  // One hybrid snapshot for both the prefetch fetcher and the cache mode, so the
  // entry ThoughtDetail later consumes is keyed under the same mode it was
  // prefetched with.
  const hybrid = hybridTagsEnabled();
  const mode = tagSuggestionMode('existingTags', hybrid);
  prefetchTagSuggestions(
    thought.id,
    mode,
    existingTags,
    (signal) =>
      suggestTagsForThought(thought.thought, allTags, existingTags, { signal, hybrid })
  );
  return () => {
    if (activeIdRef && activeIdRef.current === thought.id) return;
    abortTagSuggestionsForId(thought.id, mode);
  };
};
